/**
 * V5.1 — Marktphasen-Engine.
 *
 * Drei orthogonale Achsen:
 * - Trendregime (auf SPY/MSCI World): bull / range / bear
 * - Volatilitätsregime (auf VIX): low_vol / normal / high_vol / panic
 * - Sektorrotation: risk_on_growth / risk_on_value / risk_off_quality / risk_off_safe_haven / theme_dominant
 *
 * Aggregierte Phase (5 Phasen): bull / korrektur / bear / accumulation / neutral.
 * Welle 6: Erweitert von 3 auf 5 Phasen für feinere Score-Gewichtung.
 *
 * Pure-Function. KI ist Erklärer (Tagesbriefing-Cron), nicht Entscheider —
 * die Achsen sind regelbasiert berechnet.
 */

import type { V51StrategyId } from './types';

export type TrendRegime = 'bull' | 'range' | 'bear';
export type VolatilityRegime = 'low_vol' | 'normal' | 'high_vol' | 'panic';
export type SectorRotation =
  | 'risk_on_growth'
  | 'risk_on_value'
  | 'risk_off_quality'
  | 'risk_off_safe_haven'
  | 'theme_dominant'
  | 'unknown';

/**
 * 5 Marktphasen mit klarer Trader-Semantik:
 * - bull: Klarer Aufwärtstrend, niedrige Vol → Breakouts/Momentum führen
 * - korrektur: Aufwärtstrend pausiert, Vol erhöht → Pullback-Setups, kleinere Sizes
 * - bear: Aktiver Abwärtstrend mit Angst → defensive only, kaum neue Trades
 * - accumulation: Bear beruhigt sich (vol normal/low) → Long-Term-Einstieg
 * - neutral: Keine klare Richtung → selektive Quality-Pullbacks
 */
export type AggregatedPhase = 'bull' | 'korrektur' | 'bear' | 'accumulation' | 'neutral';

export interface MarketPhaseInput {
  /** SPY current price */
  spy_price: number | null;
  /** SPY MA200 */
  spy_ma200: number | null;
  /** SPY MA50 */
  spy_ma50: number | null;
  /** SPY MA200 Slope (% pro Woche) — positiv = bull */
  spy_ma200_slope_pct_per_week: number | null;
  /** SPY ADX 14 */
  spy_adx14: number | null;
  /** VIX current */
  vix_level: number | null;
  /** VIX 20-Tage-Hoch (für Spike-Detection) */
  vix_20d_high: number | null;
  /** W94 — SPY-13W-Return (%) für echtes Relative-Strength-Rating. */
  spy_return_13w_pct?: number | null;

  /** Sektor-RS-Werte (XLK, XLY, XLP, XLU, XLV, XLE, XLF) */
  sector_rs?: Record<string, number>;
  /** Edelmetall-Performance (GLD relativ zu SPY) */
  gld_vs_spy_3m_pct?: number;
  /** Theme-Performance (NVDA-Korb relativ zu SPY) */
  ai_vs_spy_3m_pct?: number;
}

export interface MarketPhaseResult {
  trend: TrendRegime;
  volatility: VolatilityRegime;
  rotation: SectorRotation;
  aggregate: AggregatedPhase;
  /** Klartext-Erklärung der Lage */
  summary: string;
  /** Welche Strategien sind in dieser Phase bevorzugt */
  preferred_strategies: V51StrategyId[];
  /** Welche Watchlist-Module sollten priorisiert werden */
  preferred_watchlist_roles: string[];
  /** Welche Achse hat sich zuletzt gewandelt? (Heuristik) */
  hint_for_user: string;
  /** W92 — VIX-Level zum Berechnungszeitpunkt (Input durchgereicht), damit
   *  Konsumenten (Phantom-Recorder vix_at_generation, Sizing-Brake) den Wert
   *  nicht separat laden müssen. */
  vix_level: number | null;
  /** W94 — SPY-13W-Return (Input durchgereicht) für echtes RS-Rating in
   *  buildStrategyFeatures (Symbol-Return minus Markt-Return). */
  spy_return_13w_pct: number | null;
}

export function computeMarketPhase(input: MarketPhaseInput): MarketPhaseResult {
  // ===== Trend =====
  // Welle 6: ADX-Schwelle 20→15 (frühe Stage-2-Trends nicht ausschließen).
  let trend: TrendRegime = 'range';
  if (input.spy_price !== null && input.spy_ma200 !== null && input.spy_ma200_slope_pct_per_week !== null) {
    const aboveMa = input.spy_price > input.spy_ma200;
    const slope = input.spy_ma200_slope_pct_per_week;
    const adx = input.spy_adx14 ?? 0;
    if (aboveMa && slope > 0 && adx >= 15) trend = 'bull';
    else if (!aboveMa && slope < 0) trend = 'bear';
    else trend = 'range';
  }

  // ===== Volatilität =====
  let volatility: VolatilityRegime = 'normal';
  if (input.vix_level !== null) {
    const v = input.vix_level;
    const spike = input.vix_20d_high !== null && v > input.vix_20d_high * 1.5;
    if (spike || v > 30) volatility = 'panic';
    else if (v > 22) volatility = 'high_vol';
    else if (v < 16) volatility = 'low_vol';
    else volatility = 'normal';
  }

  // ===== Sektorrotation =====
  let rotation: SectorRotation = 'unknown';
  if (input.sector_rs) {
    const xlk = input.sector_rs['XLK'] ?? 50;
    const xly = input.sector_rs['XLY'] ?? 50;
    const xlp = input.sector_rs['XLP'] ?? 50;
    const xlu = input.sector_rs['XLU'] ?? 50;
    const xlv = input.sector_rs['XLV'] ?? 50;
    const xle = input.sector_rs['XLE'] ?? 50;
    const xlf = input.sector_rs['XLF'] ?? 50;

    const growthAvg = (xlk + xly) / 2;
    const valueAvg = (xle + xlf) / 2;
    const defensiveAvg = (xlp + xlu + xlv) / 3;

    // Theme-Dominanz?
    if (input.ai_vs_spy_3m_pct !== undefined && input.ai_vs_spy_3m_pct > 15) {
      rotation = 'theme_dominant';
    } else if (input.gld_vs_spy_3m_pct !== undefined && input.gld_vs_spy_3m_pct > 10) {
      rotation = 'risk_off_safe_haven';
    } else if (defensiveAvg > growthAvg + 10 && defensiveAvg > valueAvg + 5) {
      rotation = 'risk_off_quality';
    } else if (growthAvg > valueAvg + 5 && growthAvg > defensiveAvg + 5) {
      rotation = 'risk_on_growth';
    } else if (valueAvg > growthAvg + 5 && valueAvg > defensiveAvg + 5) {
      rotation = 'risk_on_value';
    }
  }

  // ===== Aggregat (5 Phasen) =====
  // Reihenfolge wichtig — höchste Priorität zuerst.
  let aggregate: AggregatedPhase = 'neutral';
  const volElevated = volatility === 'high_vol' || volatility === 'panic';
  const rotDefensive = rotation === 'risk_off_quality' || rotation === 'risk_off_safe_haven';
  // rotOffensive entfiel mit dem Audit-Fix 07.07. (offensive Rotation ist
  // keine Bull-Pflicht mehr; nur defensive Rotation bremst).

  if (trend === 'bear' && volElevated) {
    // Aktiver Abwärtstrend mit Angst
    aggregate = 'bear';
  } else if (trend === 'bear' && !volElevated) {
    // Bear beruhigt sich — Akkumulations-Fenster für Long-Term
    aggregate = 'accumulation';
  } else if (trend === 'bull' && !volElevated && !rotDefensive) {
    // Klarer Bull: Trend up + niedrige/normale Vol. Funktions-Audit 07.07.:
    // Vorher war OFFENSIVE Rotation Pflicht — bei knapper/unbekannter Rotation
    // kippte das Aggregat auf „Seitwärts", während der summary-Text ehrlich
    // „Aufwärtstrend" sagte (Widerspruch auf einem Bildschirm; der Server-Cron
    // ohne Sektor-Daten konnte NIE bull melden). Jetzt bremst nur eine klar
    // DEFENSIVE Rotation (→ Korrektur-Zweig darunter).
    aggregate = 'bull';
  } else if ((trend === 'bull' || trend === 'range') && (volElevated || rotDefensive)) {
    // Bull-Trend mit erhöhter Vol ODER Range mit defensiver Rotation = Korrektur
    aggregate = 'korrektur';
  } else {
    aggregate = 'neutral';
  }

  // ===== Bevorzugte Strategien (Strategie-Phasen-Matrix) =====
  let preferred: V51StrategyId[] = [];
  let preferredRoles: string[] = [];
  switch (aggregate) {
    case 'bull':
      // Fusion 07.07.: relative_strength/event stillgelegt — hier gestrichen,
      // sonst empfiehlt das Phasen-Panel tote Strategien.
      preferred = ['breakout', 'pullback', 'etf_rotation', 'crypto'];
      preferredRoles = ['tactical', 'theme', 'crypto'];
      break;
    case 'korrektur':
      preferred = ['pullback', 'longterm', 'defensive'];
      preferredRoles = ['core', 'defensive'];
      break;
    case 'bear':
      preferred = ['defensive'];
      preferredRoles = ['defensive', 'core'];
      break;
    case 'accumulation':
      preferred = ['longterm', 'pullback', 'defensive'];
      preferredRoles = ['core', 'defensive'];
      break;
    case 'neutral':
    default:
      preferred = ['pullback', 'longterm'];
      preferredRoles = ['core', 'tactical'];
      break;
  }

  const summary = buildPhaseSummary(trend, volatility, rotation, aggregate);
  const hint = buildHint(aggregate, rotation);

  return {
    trend,
    volatility,
    rotation,
    aggregate,
    summary,
    preferred_strategies: preferred,
    preferred_watchlist_roles: preferredRoles,
    hint_for_user: hint,
    // W92 — Input durchreichen für Phantom-vix_at_generation + Sizing
    vix_level: input.vix_level,
    // W94 — Input durchreichen für echtes RS-Rating
    spy_return_13w_pct: input.spy_return_13w_pct ?? null,
  };
}

function buildPhaseSummary(
  t: TrendRegime,
  v: VolatilityRegime,
  r: SectorRotation,
  agg: AggregatedPhase
): string {
  const trendText = t === 'bull' ? 'Markt im Aufwärtstrend' : t === 'bear' ? 'Markt im Abwärtstrend' : 'Markt seitwärts';
  const volText =
    v === 'panic'
      ? 'Volatilität: Panik'
      : v === 'high_vol'
        ? 'Volatilität erhöht'
        : v === 'low_vol'
          ? 'Volatilität niedrig'
          : 'Volatilität normal';
  const rotText =
    r === 'risk_on_growth'
      ? 'Wachstum führt'
      : r === 'risk_on_value'
        ? 'Value/Zykliker führen'
        : r === 'risk_off_quality'
          ? 'Defensive Sektoren führen'
          : r === 'risk_off_safe_haven'
            ? 'Gold/Treasuries führen'
            : r === 'theme_dominant'
              ? 'Themen-Sektor dominiert'
              : 'Sektorrotation unklar';
  // Audit-Optimierung 07.07.: deutsches Klartext-Label statt Roh-ID im Satz
  // („Aggregierte Phase: bull" las sich wie ein Fehler).
  const aggLabel: Record<AggregatedPhase, string> = {
    bull: 'Bulle',
    korrektur: 'Korrektur',
    bear: 'Bär',
    accumulation: 'Bodenbildung',
    neutral: 'Seitwärts',
  };
  return `${trendText}. ${volText}. ${rotText}. Gesamtbild: ${aggLabel[agg]}.`;
}

function buildHint(agg: AggregatedPhase, r: SectorRotation): string {
  if (agg === 'bull' && r === 'theme_dominant')
    return 'Theme-Werte aktuell stark — aber Konzentrations-Risiko beachten.';
  if (agg === 'bull')
    return 'Trendfolge bevorzugt. Breakouts und Momentum-Setups arbeiten. Tactical + Theme priorisieren.';
  if (agg === 'korrektur')
    return 'Aufwärtstrend pausiert. Pullback-Setups in Quality, kleinere Position-Sizes, keine FOMO-Breakouts.';
  if (agg === 'bear' && r === 'risk_off_safe_haven')
    return 'Gold/Edelmetalle führen. Schutzmodus — keine neuen Long-Trades außer Defensive.';
  if (agg === 'bear')
    return 'Aktiver Abwärtstrend mit Angst. Cash + Defensive. Keine neuen Risk-on-Trades.';
  if (agg === 'accumulation')
    return 'Bear beruhigt sich — Akkumulations-Fenster. Long-Term-Tranchen in Quality-Werten möglich.';
  return 'Gemischtes Bild. Selektive Pullback-Setups in Quality-Werten, Vorsicht bei Breakouts.';
}

/**
 * V5.1 — Strategie-Phasen-Matrix (Live-Score-Multiplier).
 * Wert 0-100 — wie gut passt die Strategie zur aktuellen Marktphase.
 *
 * Welle 6: 5 Phasen statt 3 — feinere Unterscheidung zwischen
 * Korrektur (Pullback in Bull) und Bear (echter Abwärtstrend),
 * sowie Accumulation (oversold + ruhig) als eigene Long-Term-Phase.
 */
export function marketPhaseFitFor(strategyId: V51StrategyId, phase: AggregatedPhase): number {
  const matrix: Record<AggregatedPhase, Record<V51StrategyId, number>> = {
    bull: {
      breakout: 100,
      pullback: 90,
      relative_strength: 100,
      event: 90,
      // W91-Audit — 40 → 65: Fit 40 machte Quality-/Aristokraten-Dips im
      // Bull strukturell unsichtbar (roh 95 × 0.40 = 38 = "Schwach"), obwohl
      // Einzelwert-Rücksetzer der antizyklische Kernfall sind. 65 hält die
      // Trend-Strategien vorne, ohne den Dip-Pfad zu töten.
      longterm: 65,
      etf_rotation: 90,
      defensive: 15, // Defensive in Bull unattraktiv
      crypto: 90,
    },
    korrektur: {
      breakout: 35, // Breakouts scheitern oft in Korrektur
      pullback: 80, // Pullback-Setups arbeiten gut
      relative_strength: 60,
      event: 55,
      longterm: 75,
      etf_rotation: 55,
      defensive: 75,
      crypto: 35,
    },
    bear: {
      breakout: 10,
      pullback: 25,
      relative_strength: 20,
      event: 20,
      longterm: 55, // Vorsichtig akkumulieren, aber Vol zu hoch
      etf_rotation: 30,
      defensive: 100,
      crypto: 15,
    },
    accumulation: {
      breakout: 30,
      pullback: 65,
      relative_strength: 45,
      event: 50,
      longterm: 100, // Hauptphase für Long-Term-Tranchen
      etf_rotation: 50,
      defensive: 70,
      crypto: 60, // BTC oft früh wieder stark
    },
    neutral: {
      breakout: 60,
      pullback: 85,
      relative_strength: 80,
      event: 70,
      longterm: 75,
      etf_rotation: 70,
      defensive: 50,
      crypto: 55,
    },
  };
  return matrix[phase][strategyId];
}

// =============================================================
// W75.6 — Eigene Crypto-Marktphase (Audit Agent F HIGH).
// =============================================================
// Bisher nutzte die Crypto-Strategie SPY-getriebenen aggregate-Phase.
// Crypto kann aber Bull sein waehrend SPY Bear ist (entkoppelt).
// Diese Funktion liefert eine Krypto-eigene Phase basiered auf:
//   - BTC vs MA200 (Daily-Proxy via pct_above_ma200)
//   - BTC-Dominanz (steigend = Alts schwach, fallend = Alt-Season)
//   - Drawdown vom ATH (fuer Cycle-Position)
// Output: 'crypto_bull' | 'crypto_correction' | 'crypto_bear' | 'crypto_accumulation' | 'crypto_alt_season'

export type CryptoMarketPhase =
  | 'crypto_bull'
  | 'crypto_correction'
  | 'crypto_bear'
  | 'crypto_accumulation'
  | 'crypto_alt_season';

export interface CryptoMarketPhaseInput {
  /** BTC pct above MA200 in % (z.B. +12 = bull, -8 = bear) */
  btc_pct_above_ma200: number | null;
  /** BTC-Dominanz in % (z.B. 56) */
  btc_dominance_pct: number | null;
  /** Drawdown vom ATH in % (negativ, z.B. -38 fuer 38% unter ATH) */
  btc_drawdown_from_ath_pct: number | null;
  /** Optional Fear-and-Greed-Index (0-100) — wenn vorhanden */
  fear_greed_index?: number | null;
}

export interface CryptoMarketPhaseResult {
  phase: CryptoMarketPhase;
  summary: string;
  /** Welche Strategien sind in dieser Phase bevorzugt (Crypto-spezifisch) */
  preferred_action: 'long_btc' | 'long_alts' | 'wait_accumulation' | 'cash';
}

export function computeCryptoMarketPhase(input: CryptoMarketPhaseInput): CryptoMarketPhaseResult {
  const above200 = input.btc_pct_above_ma200;
  const dominance = input.btc_dominance_pct;
  const dd = input.btc_drawdown_from_ath_pct;
  const fg = input.fear_greed_index ?? null;

  // crypto_bear — Preis < MA200 + Fear < 25 (Capitulation)
  if (above200 !== null && above200 < -10 && (fg === null || fg < 25)) {
    return {
      phase: 'crypto_bear',
      summary: 'BTC unter MA200 + extreme Angst — Risk-Off, kein Long-Setup',
      preferred_action: 'cash',
    };
  }
  // crypto_accumulation — DD > -50% + lang im Bottom + Fear sehr niedrig
  if (dd !== null && dd < -50 && (fg === null || fg < 30)) {
    return {
      phase: 'crypto_accumulation',
      summary: 'BTC >50% unter ATH + extreme Angst — Akkumulationszone',
      preferred_action: 'wait_accumulation',
    };
  }
  // crypto_alt_season — BTC.D < 52 + fallend + BTC stabil
  if (dominance !== null && dominance < 52 && above200 !== null && above200 > -5) {
    return {
      phase: 'crypto_alt_season',
      summary: 'BTC-Dominanz <52% — Alts erhalten Momentum, Long-Alts-Bias',
      preferred_action: 'long_alts',
    };
  }
  // crypto_correction — Preis -10 bis -30% vom ATH, Fear 30-50
  if (dd !== null && dd <= -10 && dd > -35) {
    return {
      phase: 'crypto_correction',
      summary: `BTC ${dd.toFixed(0)}% unter ATH — Korrektur-Phase, Selektiv`,
      preferred_action: 'wait_accumulation',
    };
  }
  // crypto_bull — Preis > MA200 + BTC.D moderate + DD < -15%
  if (above200 !== null && above200 > 0 && (dd === null || dd > -20)) {
    return {
      phase: 'crypto_bull',
      summary: 'BTC > MA200 + nahe ATH — Bull-Phase, Long-BTC-Bias',
      preferred_action: 'long_btc',
    };
  }
  // Default: korrektur
  return {
    phase: 'crypto_correction',
    summary: 'Crypto-Markt-Daten unklar — vorsichtige Korrektur-Annahme',
    preferred_action: 'wait_accumulation',
  };
}
